import { Badge, Button, DataTable, PageHeader, Skeleton, useToast } from '@zyxantech/tux'
import { FileText, Plus, RefreshCw, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  useDeleteReportMutation,
  useGenerateReportMutation,
  useGetReportsQuery,
  type Report,
} from '@/api/reports.api'
import { EmptyState } from '@/components/shared/EmptyState'
import { ErrorState } from '@/components/shared/ErrorState'
import { ACTION, RESOURCE } from '@/constants/permissions'
import { ROUTES } from '@/constants/routes'
import { usePermission } from '@/hooks/usePermission'
import { formatTimestamp } from '@/utils/format'
import { buildPath } from '@/utils/url'
import { ReportRunsDrawer } from './components/ReportRunsDrawer'

const FORMAT_LABEL: Record<string, string> = {
  pdf: 'PDF',
  csv: 'CSV',
  xlsx: 'Excel',
}

const FORMAT_VARIANT = {
  pdf: 'danger',
  csv: 'success',
  xlsx: 'info',
} as const

function formatOf(report: Report): string {
  const config = report.config as { format?: string }
  return FORMAT_LABEL[config.format ?? ''] ?? (config.format ?? '—')
}

function tagCountOf(report: Report): number {
  const config = report.config as { tagIds?: unknown[] }
  return config.tagIds?.length ?? 0
}

export function ReportsPage() {
  const navigate = useNavigate()
  const toast = useToast()
  const canCreate = usePermission(RESOURCE.REPORT, ACTION.CREATE)
  const canDelete = usePermission(RESOURCE.REPORT, ACTION.DELETE)

  const [runsReportId, setRunsReportId] = useState<string | null>(null)
  const [runsReportName, setRunsReportName] = useState('')
  const [generatingId, setGeneratingId] = useState<string | null>(null)

  const { data, isLoading, error, refetch } = useGetReportsQuery()
  const [deleteReport] = useDeleteReportMutation()
  const [generate] = useGenerateReportMutation()

  const handleGenerate = async (report: Report) => {
    setGeneratingId(report.id)
    try {
      await generate(report.id).unwrap()
      toast.show('Report queued — check Runs for status', 'success')
    } catch {
      toast.show('Failed to queue report', 'error')
    } finally {
      setGeneratingId(null)
    }
  }

  const handleDelete = async (id: string) => {
    try {
      await deleteReport(id).unwrap()
      toast.show('Report deleted', 'success')
    } catch {
      toast.show('Failed to delete report', 'error')
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton height={28} width="25%" />
        <Skeleton height={300} />
      </div>
    )
  }
  if (error) {
    return (
      <div className="p-6">
        <ErrorState error={error} retry={() => void refetch()} />
      </div>
    )
  }

  const reports = data?.items ?? []

  return (
    <div className="p-6">
      <PageHeader
        title="Reports"
        subtitle="Create and schedule data export reports"
        actions={
          canCreate ? (
            <Button
              variant="primary"
              icon={<Plus size={14} />}
              onClick={() => navigate(ROUTES.REPORT_NEW)}
            >
              New report
            </Button>
          ) : undefined
        }
      />

      {reports.length === 0 ? (
        <EmptyState
          title="No reports yet"
          message="Create a report to schedule recurring data exports."
          action={
            canCreate ? (
              <Button
                variant="primary"
                icon={<Plus size={14} />}
                onClick={() => navigate(ROUTES.REPORT_NEW)}
              >
                New report
              </Button>
            ) : undefined
          }
        />
      ) : (
        <DataTable
          columns={[
            {
              key: 'name',
              header: 'Name',
              accessor: (r) => (
                <div className="flex items-center gap-2">
                  <FileText size={14} className="shrink-0 text-text-muted" />
                  <span className="font-medium text-text-primary">{r.name}</span>
                </div>
              ),
            },
            {
              key: 'format',
              header: 'Format',
              accessor: (r) => {
                const config = r.config as { format?: string }
                const fmt = (config.format ?? 'csv') as keyof typeof FORMAT_VARIANT
                const variant = fmt in FORMAT_VARIANT ? FORMAT_VARIANT[fmt] : ('default' as const)
                return <Badge variant={variant}>{formatOf(r)}</Badge>
              },
            },
            {
              key: 'tags',
              header: 'Tags',
              accessor: (r) => (
                <span className="text-sm text-text-secondary">{tagCountOf(r)} tag(s)</span>
              ),
            },
            {
              key: 'schedule',
              header: 'Schedule',
              accessor: (r) => (
                <span className="font-mono text-xs text-text-secondary">{r.schedule ?? '—'}</span>
              ),
            },
            {
              key: 'updatedAt',
              header: 'Last modified',
              accessor: (r) => (
                <span className="text-sm text-text-secondary">{formatTimestamp(r.updatedAt)}</span>
              ),
            },
            {
              key: 'actions',
              header: '',
              align: 'right',
              accessor: (r) => (
                <div className="flex items-center justify-end gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    icon={<RefreshCw size={13} />}
                    loading={generatingId === r.id}
                    title="Generate now"
                    onClick={(e) => {
                      e.stopPropagation()
                      void handleGenerate(r)
                    }}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation()
                      setRunsReportId(r.id)
                      setRunsReportName(r.name)
                    }}
                  >
                    Runs
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation()
                      navigate(buildPath(ROUTES.REPORT_EDIT, { id: r.id }))
                    }}
                  >
                    Edit
                  </Button>
                  {canDelete && (
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={<Trash2 size={13} />}
                      title="Delete"
                      className="text-danger hover:text-danger"
                      onClick={(e) => {
                        e.stopPropagation()
                        void handleDelete(r.id)
                      }}
                    />
                  )}
                </div>
              ),
            },
          ]}
          data={reports}
          rowKey={(r) => r.id}
        />
      )}

      {runsReportId && (
        <ReportRunsDrawer
          reportId={runsReportId}
          reportName={runsReportName}
          onClose={() => setRunsReportId(null)}
        />
      )}
    </div>
  )
}
